import { useCallback } from 'react';
import { postFieldAuditEntry } from '../service/fieldAudit';

/**
 * Installs a `fieldChanged` listener that POSTs one
 * `DocumentFieldAudit` row per user edit on a template-merged document.
 *
 * Scoped to post-merge documents only — caller (TabView) must guard on
 * `location.state?.fromTemplateMerge === true` before installing.
 *
 * `originalFieldValues` is the key→value snapshot captured at merge
 * time (forwarded through the `openFile` navigation state). Edits whose
 * new value equals the snapshot are treated as idempotent and skipped —
 * we only audit real changes.
 *
 * Coalescing strategy
 * -------------------
 * Apryse dispatches `fieldChanged` synchronously to all listeners; when
 * `useFieldFormatValidator` calls `field.setValue()` from inside its own
 * listener, the canonical-value event is dispatched re-entrantly before
 * control returns to the audit listener for the raw-value event. Any
 * scheme that reasons about *which* event we're on (validator-aware
 * skipping, "is this normalizable?" predicates) is fragile under that
 * dispatch order — Bugbot PR #66 caught two distinct phantom-row bugs
 * built on those assumptions.
 *
 * Instead we use a pure per-field debounce: every `fieldChanged` event,
 * regardless of source or validator state, replaces any pending POST
 * for that field with a fresh timer carrying the latest value. Last
 * value wins, by construction. Re-entrancy is harmless because the
 * timer hasn't fired yet when the canonical event re-enters; the raw
 * value's `setTimeout` is simply discarded and replaced.
 */
const useTemplateMergeFieldAudit = () => {
  const installFieldAuditListener = useCallback((instance, documentId, originalFieldValues = {}) => {
    if (!instance?.Core || !documentId) return () => {};
    const { annotationManager } = instance.Core;
    if (!annotationManager?.addEventListener) return () => {};

    // Last value we POSTed (or the merge-time snapshot for fields the
    // user hasn't touched yet) — used to suppress no-op audits when a
    // field-changed event arrives but the value is unchanged from what
    // we already recorded.
    const lastSent = new Map(Object.entries(originalFieldValues || {}));

    // Pending debounced POSTs, keyed by fieldKey.
    const pendingPosts = new Map();

    // Tunable: how long we wait after the *last* fieldChanged event for
    // a given field before POSTing. Short enough to be invisible to a
    // user still typing, long enough to coalesce a re-entrant
    // raw-input + validator-rewrite pair into a single audit row.
    const DEBOUNCE_WINDOW_MS = 75;

    const firePost = (fieldKey, fieldName, value) => {
      const previous = lastSent.has(fieldKey) ? String(lastSent.get(fieldKey) ?? '') : '';
      if (value === previous) return;
      lastSent.set(fieldKey, value);
      postFieldAuditEntry(documentId, { fieldKey, fieldName, value });
    };

    const handleFieldChanged = (field, newValue) => {
      try {
        if (!field) return;
        const fieldKey = field.name || field.getName?.();
        if (!fieldKey) return;

        const value = newValue == null ? '' : String(newValue);
        const fieldName =
          field.widgets?.[0]?.getCustomData?.('trn-field-name') || fieldKey;

        // Always cancel any pending POST and reschedule with the latest
        // value — last value wins. Whether this is the raw-input event,
        // the validator's normalized rewrite, an already-canonical
        // single emit, an invalid input, or a user just typing fast,
        // they all flow through the same path.
        const existing = pendingPosts.get(fieldKey);
        if (existing) clearTimeout(existing.timer);

        const timer = setTimeout(() => {
          pendingPosts.delete(fieldKey);
          firePost(fieldKey, fieldName, value);
        }, DEBOUNCE_WINDOW_MS);

        pendingPosts.set(fieldKey, { timer, value, fieldName });
      } catch (err) {
        console.warn('[template-merge-audit] fieldChanged handler failed:', err);
      }
    };

    annotationManager.addEventListener('fieldChanged', handleFieldChanged);

    return () => {
      try {
        annotationManager.removeEventListener?.('fieldChanged', handleFieldChanged);
      } catch (_) { /* noop */ }
      // Flush any pending debounced POSTs synchronously on cleanup so
      // a tab close mid-debounce doesn't drop the user's last edit.
      pendingPosts.forEach((pending, fieldKey) => {
        try {
          clearTimeout(pending.timer);
          firePost(fieldKey, pending.fieldName, pending.value);
        } catch (err) {
          console.warn('[template-merge-audit] flush pending failed:', err);
        }
      });
      pendingPosts.clear();
    };
  }, []);

  return { installFieldAuditListener };
};

export default useTemplateMergeFieldAudit;
